import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Cookie, X, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CONSENT_KEY = 'calvio_cookie_consent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden">
            {!showSettings ? (
              /* Main Banner */
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F7F4] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-[#76A686]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#2D3748] mb-2">
                      {t('cookies_banner.title')}
                    </h3>
                    <p className="text-sm text-[#718096] mb-4">
                      {t('cookies_banner.description')}{' '}
                      <Link to="/cookies" className="text-[#76A686] hover:underline">
                        {t('footer.cookies')}
                      </Link>.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={handleAcceptAll}
                        className="btn-primary"
                      >
                        {t('cookies_banner.accept_all')}
                      </Button>
                      <Button
                        onClick={handleAcceptNecessary}
                        variant="outline"
                        className="border-stone-200"
                      >
                        {t('cookies_banner.necessary_only')}
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="text-[#718096]"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t('cookies_banner.manage')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Settings Panel */
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-[#2D3748]">{t('cookies_banner.preferences_title')}</h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-[#718096] hover:text-[#2D3748]"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div>
                      <h4 className="font-medium text-[#2D3748]">{t('cookies_banner.necessary_title')}</h4>
                      <p className="text-sm text-[#718096]">
                        {t('cookies_banner.necessary_desc')}
                      </p>
                    </div>
                    <div className="bg-[#76A686] text-white text-xs px-3 py-1 rounded-full">
                      {t('cookies_banner.always_active')}
                    </div>
                  </div>
                  
                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div>
                      <h4 className="font-medium text-[#2D3748]">{t('cookies_banner.analytics_title')}</h4>
                      <p className="text-sm text-[#718096]">
                        {t('cookies_banner.analytics_desc')}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#76A686]"></div>
                    </label>
                  </div>
                  
                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div>
                      <h4 className="font-medium text-[#2D3748]">{t('cookies_banner.marketing_title')}</h4>
                      <p className="text-sm text-[#718096]">
                        {t('cookies_banner.marketing_desc')}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#76A686]"></div>
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="btn-primary"
                  >
                    {t('cookies_banner.save')}
                  </Button>
                  <Button
                    onClick={handleAcceptAll}
                    variant="outline"
                  >
                    {t('cookies_banner.accept_all')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
